// src/pages/statistics/TopUsersByProjectsTable.tsx
import React from "react";
import { useQuery, gql } from "@apollo/client";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Typography,
  CircularProgress,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const GET_TOP_USERS_BY_PROJECTS = gql`
  query GetTopUsersByProjects($limit: Int = 10) {
    getTopUsersByProjects(limit: $limit) {
      id
      firstName
      lastName
      projectCount
      totalPoles
    }
  }
`;

type TopUser = {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  projectCount: number;
  totalPoles?: number | null;
};

type TopUsersResp = {
  getTopUsersByProjects: TopUser[];
};

const TopUsersByProjectsTable: React.FC = () => {
  const navigate = useNavigate();
  const [limit, setLimit] = React.useState<number>(10);

  const { data, loading, error, refetch } = useQuery<TopUsersResp>(
    GET_TOP_USERS_BY_PROJECTS,
    { variables: { limit } }
  );

  const rows = data?.getTopUsersByProjects ?? [];

  const handleRowOpen = (u: TopUser) => {
    if (!u.id) return;
    navigate(`/${u.id}/projects`);
  };

  return (
    <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
      <Toolbar sx={{ gap: 2, flexWrap: "wrap" }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Top Users by Projects
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="limit-users-label" color="secondary">
            Limit
          </InputLabel>
          <Select
            labelId="limit-users-label"
            label="Limit"
            value={limit}
            onChange={(e) => {
              const v = Number(e.target.value);
              setLimit(v);
              refetch({ limit: v });
            }}
            color="secondary"
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>
      </Toolbar>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box sx={{ p: 2 }}>
          <Typography color="error">
            {error.message || "Failed to load table"}
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>User</TableCell>
                <TableCell align="right">Projects</TableCell>
                <TableCell align="right">Total Poles (all projects)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((u, idx) => {
                const fullName =
                  [u.firstName, u.lastName].filter(Boolean).join(" ") || "—";
                return (
                  <TableRow
                    hover
                    key={u.id}
                    onClick={() => handleRowOpen(u)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleRowOpen(u);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Open projects of ${fullName}`}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{fullName}</TableCell>
                    <TableCell align="right">{u.projectCount}</TableCell>
                    <TableCell align="right">{u.totalPoles || 0}</TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography
                      sx={{ p: 2, textAlign: "center", opacity: 0.7 }}
                    >
                      No data
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default TopUsersByProjectsTable;
